/**
 * Shared mui style confirm dialog. The ConfirmDialogManager should be rendered once in app.js
 *
 * How to use:
 *  import {confirm} from '@/features/shared/components/confirm'
 *
 *    confirm('admin.widgets.remove_confirm').then( (result) => {} )
 *  or:
 *    result = await confirm('admin.widgets.remove_confirm')
 *
 * NOTES
 *  Only one can be shown at a time. If a new one is opened the current one will result in a cancel.
 */

import { useCallback, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
} from "@mui/material";

import { useTranslation } from "@/lib/i18n";

export async function confirm(title: string, body: string): Promise<boolean | undefined> {
  if (!showConfirm) {
    console.error("<ConfirmDialogManager /> not included");
    return;
  }

  return showConfirm({ title, body });
}

let showConfirm: (options: ConfirmOptions) => Promise<boolean> | undefined;

type ConfirmOptions = {
  title: string;
  body: string;
};

// eslint-disable-next-line react/display-name
export const ConfirmDialogManager = () => {
  const { t } = useTranslation();
  const callBack = useRef<(ar1: boolean) => void | undefined>();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | undefined>();

  const handleClose = () => {
    resolveActiveConfirm(false);
    setOpen(false);
  };

  const handleConfirm = () => {
    resolveActiveConfirm(true);
    setOpen(false);
  };

  const resolveActiveConfirm = useCallback((result: boolean) => {
    if (callBack.current) callBack.current(result);

    callBack.current = undefined;
  }, []);

  const show = useCallback((options: ConfirmOptions) => {
    // Resolve the currently open confirm with false
    resolveActiveConfirm(false);

    // Apply the new config
    setOptions(options);
    setOpen(true);

    return new Promise<boolean>((resolve) => {
      callBack.current = resolve;
    });
  }, []);

  // Link the showConfirm outside the component to the one inside of the component
  showConfirm = show;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{options?.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">{options?.body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("general.confirm.cancel")}</Button>
        <Button onClick={handleConfirm} autoFocus>
          {t("general.confirm.ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
